import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHttpUpdateProjectStatus } from '../../../hooks';
import { Button, RadioGroup } from '../../Common';
import { dispatch } from '../../../store';
import { ProjectStatus } from '@cyberlabrs/helpers';
import { selectCachedProjects, updateCachedProject } from '../../../store/slices/projects.slice';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { DateTime } from 'luxon';
import DatePickerSimple from './DatePickerSimple';

interface IChangeProjectStatus {
    close: () => void;
    projectId: string;
    currentStatus: ProjectStatus;
    currentEndDate?: string | null;
    onSuccess?: () => void;
}

const REOPEN_STATUSES = [ProjectStatus.KICK_OFF, ProjectStatus.IN_PROGRESS];

const ChangeProjectStatus = ({ close, projectId, currentStatus, currentEndDate, onSuccess }: IChangeProjectStatus) => {
    const todayIso = DateTime.now().toISODate()!;
    const tomorrowIso = DateTime.now().plus({ days: 1 }).toISODate()!;
    const projects = useSelector(selectCachedProjects);
    const oldProject = projects.find(p => p.id === projectId);
    const [selectedStatus, setSelectedStatus] = useState<ProjectStatus>(currentStatus);
    const [newEndDate, setNewEndDate] = useState<DateTime | null>(DateTime.now());
    const [endDateError, setEndDateError] = useState<string | null>(null);

    const { exec: updateProjectStatus } = useHttpUpdateProjectStatus();

    const currentEndDateIso = currentEndDate ? DateTime.fromISO(currentEndDate).toISODate() : null;
    const isEndDateInPast = !!currentEndDateIso && currentEndDateIso < todayIso;

    const isReopeningFromCompleted = currentStatus === ProjectStatus.COMPLETED && REOPEN_STATUSES.includes(selectedStatus);

    const isReopeningFromOnHold = currentStatus === ProjectStatus.ON_HOLD && REOPEN_STATUSES.includes(selectedStatus) && isEndDateInPast;

    const isManuallyCompleting = currentStatus !== ProjectStatus.COMPLETED && selectedStatus === ProjectStatus.COMPLETED;

    const showDatePicker = isReopeningFromCompleted || isReopeningFromOnHold || isManuallyCompleting;

    const projectStatusMenu = [
        { value: ProjectStatus.KICK_OFF, label: 'Kick Off' },
        { value: ProjectStatus.IN_PROGRESS, label: 'In Progress' },
        { value: ProjectStatus.ON_HOLD, label: 'On Hold' },
        { value: ProjectStatus.CANCELLED, label: 'Cancelled' },
        { value: ProjectStatus.COMPLETED, label: 'Completed' },
    ];

    const handleStatusChange = (status: ProjectStatus) => {
        setSelectedStatus(status);
        setNewEndDate(DateTime.now());
        setEndDateError(null);
    };

    const handleChangeStatus = () => {
        const dateIso = newEndDate?.toISODate() ?? null;

        if (isReopeningFromCompleted || isReopeningFromOnHold) {
            if (!dateIso) {
                setEndDateError('New end date is required when reopening this project');
                return;
            }
            if (dateIso <= todayIso) {
                setEndDateError('End date must be in the future');
                return;
            }
        }

        if (isManuallyCompleting) {
            if (!dateIso) {
                setEndDateError('End date is required');
                return;
            }
            if (dateIso > todayIso) {
                setEndDateError('End date cannot be in the future');
                return;
            }
        }

        const endDate = showDatePicker && dateIso ? dateIso : undefined;

        updateProjectStatus(
            projectId,
            selectedStatus,
            () => {
                const updatedProject = {
                    ...oldProject,
                    projectStatus: selectedStatus as ProjectStatus,
                    ...(endDate && { endDate }),
                };
                dispatch(updateCachedProject(updatedProject as ProjectDtoRelV2));
                onSuccess?.();
            },
            undefined,
            endDate
        );
        close();
    };

    return (
        <div className="change-project-status">
            <div className="change-project-status__header">
                <h3 className="change-project-status__title">Change Project Status</h3>
                <p className="change-project-status__description">
                    This will immediately update the project's status and apply the change across all related views.
                </p>
            </div>
            <div className="edit-role__radio">
                <RadioGroup options={projectStatusMenu} value={selectedStatus} onChange={e => handleStatusChange(e as ProjectStatus)} />
            </div>
            {showDatePicker && (
                <DatePickerSimple
                    label={isManuallyCompleting ? 'Completion Date' : 'New End Date'}
                    value={newEndDate}
                    onChange={date => {
                        setNewEndDate(date);
                        setEndDateError(null);
                    }}
                    minDateIso={isReopeningFromCompleted ? tomorrowIso : undefined}
                    maxDateIso={isManuallyCompleting ? todayIso : undefined}
                    enablePastDays={isManuallyCompleting}
                    isError={!!endDateError}
                    errorMessage={endDateError ?? undefined}
                />
            )}
            <div className="edit-role__buttons">
                <Button text={'Cancel'} variant="text" onClick={close} color="blue" />
                <Button
                    text={'Save Changes'}
                    onClick={handleChangeStatus}
                    disabled={!selectedStatus || (selectedStatus === currentStatus && !showDatePicker)}
                />
            </div>
        </div>
    );
};

export default ChangeProjectStatus;
